import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deepEqual } from 'fast-equals';
import { FavoritableAssistantMessage } from 'types/chat-messages';
import { ChatContextValue } from './chat-context.slice';

export type HelpfulMessage = {
  type: 'helpfulMessage';
  question: string;
  answer: string;
  context: ChatContextValue;
  originalMessageId: string;
  isMarkedAsHelpful: boolean;
  isIncludedInThePrompt: boolean;
  queryHasBeenRunAndIsNotFatal: boolean;
  chatId: string;
};

export type NewHelpfulMessageAction = {
  type: 'new';
  message: Omit<HelpfulMessage, 'isMarkedAsHelpful' | 'queryHasBeenRunAndIsNotFatal' | 'isIncludedInThePrompt' | 'type'> & {
    chatId: string;
  };
};

export type ToggleIncludeInPromptAction = {
  type: 'toggle-include-in-prompt';
  id: string;
};

export type ToggleHelpfulAction = {
  type: 'toggle-helpful';
  message: HelpfulMessage | FavoritableAssistantMessage;
};

export type HelpfulMessageAction = NewHelpfulMessageAction | ToggleIncludeInPromptAction | ToggleHelpfulAction;

const initialState: HelpfulMessage[] = [];

export type HelpfulMessagesStoreInit = {
  value?: HelpfulMessage[];
};

export const helpfulMessagesSliceName = 'helpfulMessages';

export const helpfulMessagesSlice = createSlice({
  name: helpfulMessagesSliceName,
  initialState,
  reducers: {
    initializeHelpfulMessage: (state, action: PayloadAction<HelpfulMessagesStoreInit>) => {
      if (action.payload.value) {
        action.payload.value.forEach((message) => state.push(message));
      }
    },
    newHelpfulMessage: (state, action: PayloadAction<NewHelpfulMessageAction['message']>) => {
      state.push({
        ...action.payload,
        isMarkedAsHelpful: true,
        queryHasBeenRunAndIsNotFatal: true,
        isIncludedInThePrompt: !state.find((message) => deepEqual(message.context, action.payload.context)),
        type: 'helpfulMessage',
      });
    },
    toggleIncludeInPrompt: (state, action: PayloadAction<ToggleIncludeInPromptAction['id']>) => {
      const message = state.find((message) => message.originalMessageId === action.payload);
      const currentIsIncludeInPrompt = message?.isIncludedInThePrompt;
      state.forEach((message) => {
        message.isIncludedInThePrompt = false;
      });
      if (message) {
        message.isIncludedInThePrompt = !currentIsIncludeInPrompt;
      }
    },
    toggleHelpful: (state, action: PayloadAction<ToggleHelpfulAction['message']>) => {
      const message = state.find(
        (message) =>
          ('id' in action.payload && message.originalMessageId === action.payload.id) ||
          message.originalMessageId === action.payload.originalMessageId
      );

      if (message) {
        const newStatus = !message.isMarkedAsHelpful;
        // If we're unmarking a message as helpful, we need to remove it from the list.
        if (!newStatus) {
          state = state.filter((message) => {
            if ('id' in action.payload) {
              return message.originalMessageId !== action.payload.id;
            }
            return message.originalMessageId !== action.payload.originalMessageId;
          });

          return state;
        }

        message.isMarkedAsHelpful = newStatus;
      }
      return state;
    },
  },
});

export const { initializeHelpfulMessage, newHelpfulMessage, toggleHelpful, toggleIncludeInPrompt } = helpfulMessagesSlice.actions;

export default helpfulMessagesSlice.reducer;
